import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { AuthShell, Field } from './Login';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { isGerman } from '@/i18n/locale';

export default function Signup() {
  const signUp = useAuthStore((s) => s.signUp);
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const de = isGerman();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(de ? 'Das Passwort muss mindestens 8 Zeichen lang sein.' : 'Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    const res = await signUp(email, password, fullName.trim() || undefined);
    setLoading(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    navigate('/onboarding', { replace: true });
  };

  return (
    <AuthShell
      title={de ? 'Konto erstellen' : 'Create your account'}
      subtitle={de ? 'Ein Schritt näher zu ruhigeren 3-Uhr-Momenten.' : 'One step closer to calmer 3am moments.'}
    >
      <GoogleSignInButton
        label={de ? 'Mit Google fortfahren' : 'Continue with Google'}
        onError={(msg) => setError(msg)}
      />

      <div className="relative my-5 flex items-center">
        <div className="flex-1 h-px bg-primary-100" />
        <span className="px-3 text-[11px] uppercase tracking-wider text-slate-muted">
          {de ? 'oder' : 'or'}
        </span>
        <div className="flex-1 h-px bg-primary-100" />
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <Field
          label={de ? 'Dein Name (optional)' : 'Your name (optional)'}
          value={fullName}
          onChange={setFullName}
          autoComplete="name"
        />
        <Field
          label={de ? 'E-Mail' : 'Email'}
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          required
        />
        <Field
          label={de ? 'Passwort' : 'Password'}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          required
          minLength={8}
          hint={de ? 'Mindestens 8 Zeichen.' : 'At least 8 characters.'}
        />
        {error && (
          <div className="rounded-lg bg-danger/5 border border-danger/30 px-3 py-2 text-sm text-danger-700" role="alert">
            {error}
          </div>
        )}
        <button type="submit" disabled={loading} className="btn-primary w-full">
          {loading
            ? (de ? 'Konto wird erstellt…' : 'Creating account…')
            : (de ? 'Konto erstellen' : 'Create account')}
        </button>
      </form>
      <p className="text-sm text-slate-muted text-center mt-6">
        {de ? 'Schon ein Konto?' : 'Already have one?'}{' '}
        <Link to="/login" className="text-primary-700 hover:text-primary-800 font-medium">
          {de ? 'Anmelden' : 'Sign in'}
        </Link>
      </p>
    </AuthShell>
  );
}
